import {
  SiteSchema,
  SessionSchema,
  HeatmapResponseSchema,
  OverviewResponseSchema,
  InsightSchema,
  MemberSchema,
  type Site,
  type Session,
  type HeatmapResponse,
  type OverviewResponse,
  type Insight,
  type Member,
} from '@pulse/shared';
import { z } from 'zod';

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://pulse-api-preview.alivevietnam.com';

class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit, schema?: z.ZodSchema<T>): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let code = 'unknown';
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: { code?: string; message?: string } };
      if (body.error) {
        code = body.error.code ?? 'unknown';
        message = body.error.message ?? message;
      }
    } catch {
      /* non-JSON error */
    }
    throw new ApiError(res.status, code, message);
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json()) as unknown;
  return schema ? schema.parse(data) : (data as T);
}

const MeResponseSchema = z.object({
  member: MemberSchema,
  permissions: z.array(z.string()),
});

const SitesListSchema = z.object({ sites: z.array(SiteSchema) });
const SiteDetailSchema = z.object({ site: SiteSchema });
const SessionsListSchema = z.object({
  sessions: z.array(SessionSchema),
  cursor: z.string().optional(),
  total: z.number(),
});
const SessionDetailSchema = z.object({
  session: SessionSchema,
  insights: z.array(InsightSchema),
});

export const api = {
  me: () => request('/api/me', undefined, MeResponseSchema),

  sites: {
    list: () => request('/api/sites', undefined, SitesListSchema),
    create: (input: { name: string; domain: string }) =>
      request('/api/sites', { method: 'POST', body: JSON.stringify(input) }, SiteDetailSchema),
    get: (id: string) => request(`/api/sites/${id}`, undefined, SiteDetailSchema),
    update: (id: string, patch: Partial<{ name: string; settings: Site['settings'] }>) =>
      request(`/api/sites/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }, SiteDetailSchema),
    archive: (id: string) => request(`/api/sites/${id}`, { method: 'DELETE' }),
    overview: (id: string, range: '24h' | '7d' | '30d') =>
      request(`/api/sites/${id}/overview?range=${range}`, undefined, OverviewResponseSchema),
    heatmap: (id: string, url: string, range: '24h' | '7d' | '30d') =>
      request(
        `/api/sites/${id}/heatmap?url=${encodeURIComponent(url)}&range=${range}`,
        undefined,
        HeatmapResponseSchema,
      ),
    sessions: (id: string, opts: { limit?: number; cursor?: string } = {}) => {
      const q = new URLSearchParams();
      if (opts.limit) q.set('limit', String(opts.limit));
      if (opts.cursor) q.set('cursor', opts.cursor);
      return request(`/api/sites/${id}/sessions?${q.toString()}`, undefined, SessionsListSchema);
    },
    insights: (id: string, opts: { type?: string; range?: '24h' | '7d' | '30d' } = {}) => {
      const q = new URLSearchParams();
      if (opts.type) q.set('type', opts.type);
      if (opts.range) q.set('range', opts.range);
      return request(
        `/api/sites/${id}/insights?${q.toString()}`,
        undefined,
        z.object({ insights: z.array(InsightSchema), total: z.number() }),
      );
    },
    insightsSummary: (id: string, range: '24h' | '7d' | '30d') =>
      request(
        `/api/sites/${id}/insights/summary?range=${range}`,
        undefined,
        z.object({
          rage_click: z.object({ count: z.number(), top_selectors: z.array(z.any()) }),
          dead_click: z.object({ count: z.number(), top_selectors: z.array(z.any()) }),
          quick_back: z.object({ count: z.number(), top_urls: z.array(z.any()) }),
          error: z.object({ count: z.number(), top_messages: z.array(z.any()) }),
          excessive_scroll: z.object({ count: z.number() }),
        }),
      ),
  },

  sessions: {
    get: (id: string) => request(`/api/sessions/${id}`, undefined, SessionDetailSchema),
    replayUrl: (id: string) =>
      request(`/api/sessions/${id}/replay`, undefined, z.object({
        url: z.string(),
        size: z.number(),
        expires_at: z.number(),
      })),
  },

  members: {
    list: () => request('/api/members', undefined, z.object({ members: z.array(MemberSchema) })),
    invite: (input: { email: string; role: 'admin' | 'viewer' }) =>
      request('/api/members/invite', { method: 'POST', body: JSON.stringify(input) }, z.object({ member: MemberSchema })),
    update: (id: string, input: { role: 'admin' | 'viewer' }) =>
      request(`/api/members/${id}`, { method: 'PATCH', body: JSON.stringify(input) }, z.object({ member: MemberSchema })),
    archive: (id: string) => request(`/api/members/${id}`, { method: 'DELETE' }),
  },

  audit: {
    list: (opts: { action?: string; actor?: string; cursor?: string; limit?: number } = {}) => {
      const q = new URLSearchParams();
      if (opts.action) q.set('action', opts.action);
      if (opts.actor) q.set('actor', opts.actor);
      if (opts.cursor) q.set('cursor', opts.cursor);
      if (opts.limit) q.set('limit', String(opts.limit));
      return request(`/api/audit?${q.toString()}`, undefined, z.object({
        entries: z.array(z.any()),
        cursor: z.string().optional(),
      }));
    },
  },
};

export { ApiError };
